import React, { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertCircle, Loader2, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  TrendingUp, BarChart2, Trophy, FileText, Download, ExternalLink, Search as SearchIcon,
  Wand2, Zap
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { FeedReportsTab } from '@/components/data-quality/FeedReportsTab';

// ============================================================================
// Async Job Polling Helper
// ============================================================================

async function pollJob<T>(jobId: string, signal?: AbortSignal, intervalMs = 2000, maxAttempts = 60): Promise<T> {
  for (let i = 0; i < maxAttempts; i++) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const res = await authFetch(`/data-quality/jobs/${jobId}`);
    if (res.status === 404) throw new Error('Job not found or expired');
    if (!res.ok) throw new Error(`Polling failed (HTTP ${res.status})`);
    const json = await res.json();
    if (json.status === 'completed') return json.data as T;
    if (json.status === 'failed') throw new Error(json.error || 'Job failed');
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, intervalMs);
      if (signal) {
        signal.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
      }
    });
  }
  throw new Error('Job timed out');
}

// ============================================================================
// Types
// ============================================================================

interface IssueCounts {
  totals: { open: number; critical: number; high: number; medium: number; low: number; total: number };
  by_source: Record<string, { open: number; critical: number; total: number }>;
}

interface QualityScore {
  feed_quality_score: number | null;
  enriched_quality_score: number | null;
  final_quality_score: number | null;
  feed_completeness_pct: number | null;
  feed_correctness_pct: number | null;
  total_products: number;
  created_at: string;
}

interface UnifiedIssue {
  id: string;
  source: string;
  severity: string;
  status: string;
  computed_priority: number;
  product_name: string | null;
  category: string | null;
  tier: string | null;
  issue_type: string;
  description: string;
  field_name: string | null;
  current_value: string | null;
  expected_value: string | null;
  created_at: string;
  aeo_status: 'has_aeo' | 'aeo_target' | 'no_aeo' | 'unknown';
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface CorrectionEntry {
  id: string;
  user_email: string;
  correction_source: string;
  field_name: string;
  old_value: string;
  new_value: string;
  created_at: string;
}

interface LeaderboardEntry {
  user_email: string;
  correction_count: number;
  total_impact: number;
}

// ============================================================================
// Constants
// ============================================================================

const SOURCE_LABELS: Record<string, string> = {
  llm_judge: 'LLM Judge',
  automated: 'Automated',
  human_review: 'Human Review',
  semantic_gap: 'Semantic Gap',
  ai_eval: 'AI Eval',
};

const SOURCE_LINKS: Record<string, string> = {
  llm_judge: '/quality-audit',
  human_review: '/content-review',
  semantic_gap: '/semantic-gaps',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-blue-500 text-white',
};

// ============================================================================
// Component
// ============================================================================

export default function DataQualityDashboard() {
  const { selectedSite } = useSite();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Overview state
  const [issueCounts, setIssueCounts] = useState<IssueCounts | null>(null);
  const [qualityScore, setQualityScore] = useState<QualityScore | null>(null);
  const [breakdowns, setBreakdowns] = useState<{ by_category: any[]; by_issue_type: any[] } | null>(null);
  const [catalogReport, setCatalogReport] = useState<any>(null);

  // Issues state
  const [issues, setIssues] = useState<UnifiedIssue[]>([]);
  const [issuesPagination, setIssuesPagination] = useState<Pagination>({ total: 0, page: 1, limit: 25, pages: 0 });
  const [issueFilters, setIssueFilters] = useState({
    source: '', status: '', severity: '', category: '', tier: '', aeoStatus: '', sortBy: 'priority', sortDir: 'DESC',
  });
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [expandedIssue, setExpandedIssue] = useState<string | null>(null);
  const [issueDetail, setIssueDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Corrections state
  const [corrections, setCorrections] = useState<CorrectionEntry[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [correctionPeriod, setCorrectionPeriod] = useState('month');

  // Feed report state
  const [feedReport, setFeedReport] = useState<any>(null);

  // Correction dialog
  const [correctionDialog, setCorrectionDialog] = useState<{ open: boolean; issue: UnifiedIssue | null }>({ open: false, issue: null });
  const [correctionForm, setCorrectionForm] = useState({ field_name: '', new_value: '' });

  // Scan state
  const [scanning, setScanning] = useState(false);

  // AI suggestion state
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);

  // Date range filter
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const siteId = selectedSite?.id;

  // =========================================================================
  // Data loading
  // =========================================================================

  const loadOverview = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/data-quality/overview?siteId=${siteId}`);
      if (!res.ok) throw new Error('Failed to load overview');
      const json = await res.json();
      setIssueCounts(json.data.issue_counts);
      setQualityScore(json.data.quality_scores);
      setBreakdowns(json.data.breakdowns);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const loadCatalogReport = useCallback(async (signal?: AbortSignal) => {
    if (!siteId) return;
    try {
      const res = await authFetch(`/data-quality/catalog-report?siteId=${siteId}`);
      if (!res.ok) throw new Error('Failed to start catalog report');
      const json = await res.json();
      if (json.jobId) {
        const data = await pollJob(json.jobId, signal);
        setCatalogReport(data);
      } else {
        setCatalogReport(json.data);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return; // Component unmounted or site changed
      console.error('Failed to load catalog report:', err);
      setError(err.message);
    }
  }, [siteId]);

  const loadIssues = useCallback(async (page = 1) => {
    if (!siteId) return;
    setIssuesLoading(true);
    try {
      const params = new URLSearchParams({ siteId, page: String(page), limit: '25' });
      const filters = issueFilters;
      if (filters.source) params.set('source', filters.source);
      if (filters.status) params.set('status', filters.status);
      if (filters.severity) params.set('severity', filters.severity);
      if (filters.category) params.set('category', filters.category);
      if (filters.tier) params.set('tier', filters.tier);
      if (filters.aeoStatus) params.set('aeoStatus', filters.aeoStatus);
      params.set('sortBy', filters.sortBy);
      params.set('sortDir', filters.sortDir);

      const res = await authFetch(`/data-quality/issues?${params}`);
      if (!res.ok) throw new Error('Failed to load issues');
      const json = await res.json();
      setIssues(json.data.issues);
      setIssuesPagination(json.data.pagination);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIssuesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const loadCorrections = useCallback(async () => {
    if (!siteId) return;
    try {
      const [corrRes, lbRes] = await Promise.all([
        authFetch(`/data-quality/corrections?siteId=${siteId}&period=${correctionPeriod}&limit=50`),
        authFetch(`/data-quality/corrections/leaderboard?siteId=${siteId}&period=${correctionPeriod}`),
      ]);
      if (corrRes.ok) {
        const json = await corrRes.json();
        setCorrections(json.data.corrections);
      }
      if (lbRes.ok) {
        const json = await lbRes.json();
        setLeaderboard(json.data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  }, [siteId, correctionPeriod]);

  const loadFeedReport = useCallback(async () => {
    if (!siteId) return;
    try {
      const res = await authFetch(`/data-quality/feed-report?siteId=${siteId}`);
      if (res.ok) {
        const json = await res.json();
        setFeedReport(json.data);
      } else {
        setFeedReport({ imports: [], score_history: [], error_distribution: [] });
        setError('Failed to load feed report');
      }
    } catch (err: any) {
      setFeedReport({ imports: [], score_history: [], error_distribution: [] });
      setError(err.message);
    }
  }, [siteId]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => {
    if (activeTab !== 'overview') return;
    const controller = new AbortController();
    loadCatalogReport(controller.signal);
    return () => controller.abort();
  }, [activeTab, loadCatalogReport]);
  useEffect(() => { if (activeTab === 'issues') loadIssues(); }, [activeTab, loadIssues]);
  useEffect(() => { if (activeTab === 'corrections') loadCorrections(); }, [activeTab, loadCorrections]);
  useEffect(() => { if (activeTab === 'feed-report') loadFeedReport(); }, [activeTab, loadFeedReport]);

  // =========================================================================
  // Actions
  // =========================================================================

  const handleScanCatalog = async () => {
    if (!siteId) return;
    setScanning(true);
    setError(null);
    try {
      const res = await authFetch(`/data-quality/scan`, {
        method: 'POST',
        body: JSON.stringify({ siteId }),
      });
      if (!res.ok) throw new Error('Scan failed');
      const json = await res.json();
      if (json.jobId) {
        await pollJob(json.jobId);
      }
      await loadOverview();
      await loadCatalogReport();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const handleExpandIssue = async (issue: UnifiedIssue) => {
    const key = `${issue.source}-${issue.id}`;
    if (expandedIssue === key) {
      setExpandedIssue(null);
      setIssueDetail(null);
      return;
    }
    setExpandedIssue(key);
    setDetailLoading(true);
    try {
      const res = await authFetch(`/data-quality/issues/${issue.source}/${issue.id}?siteId=${siteId}`);
      if (res.ok) {
        const json = await res.json();
        setIssueDetail(json.data);
      }
    } catch (err) {
      console.error('Failed to load issue detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateStatus = async (issue: UnifiedIssue, newStatus: string) => {
    try {
      const res = await authFetch(`/data-quality/issues/${issue.source}/${issue.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Update failed');
      loadIssues(issuesPagination.page);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCorrectSubmit = async () => {
    if (!correctionDialog.issue) return;
    const { source, id } = correctionDialog.issue;
    try {
      const res = await authFetch(`/data-quality/issues/${source}/${id}/correct`, {
        method: 'POST',
        body: JSON.stringify({
          field_name: correctionForm.field_name,
          new_value: correctionForm.new_value,
          old_value: correctionDialog.issue.current_value,
        }),
      });
      if (!res.ok) throw new Error('Correction failed');
      setCorrectionDialog({ open: false, issue: null });
      setCorrectionForm({ field_name: '', new_value: '' });
      loadIssues(issuesPagination.page);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAISuggest = async (issue: UnifiedIssue) => {
    setAiSuggestLoading(true);
    setAiSuggestion(null);
    try {
      const res = await authFetch(`/data-quality/issues/${issue.source}/${issue.id}/ai-suggest`, { method: 'POST' });
      if (!res.ok) throw new Error('AI suggestion failed');
      const json = await res.json();
      setAiSuggestion(json.data);
      // Pre-fill correction form if suggestion available
      if (json.data.suggestion) {
        setCorrectionForm({
          field_name: json.data.field_name || issue.field_name || '',
          new_value: json.data.suggestion || json.data.expected_value || '',
        });
        setCorrectionDialog({ open: true, issue });
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAiSuggestLoading(false);
    }
  };

  const handleExportCSV = async (type: 'issues' | 'corrections') => {
    if (!siteId) return;
    try {
      const endpoint = type === 'issues'
        ? `/data-quality/issues?siteId=${siteId}&limit=10000`
        : `/data-quality/corrections?siteId=${siteId}&limit=10000`;
      const res = await authFetch(endpoint);
      if (!res.ok) return;
      const json = await res.json();
      const rows = type === 'issues' ? json.data.issues : json.data.corrections;
      if (!rows?.length) return;

      const headers = Object.keys(rows[0]);
      const csv = [
        headers.join(','),
        ...rows.map((r: any) => headers.map(h => `"${String(r[h] || '').replace(/"/g, '""')}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `data-quality-${type}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // =========================================================================
  // Render
  // =========================================================================

  if (!siteId) {
    return (
      <div className="container mx-auto py-8">
        <Alert><AlertDescription>Select a site to view data quality.</AlertDescription></Alert>
      </div>
    );
  }

  const correctionProgress = issueCounts
    ? issueCounts.totals.total > 0
      ? Math.round(((issueCounts.totals.total - issueCounts.totals.open) / issueCounts.totals.total) * 100)
      : 100
    : 0;

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Data Quality</h1>
          <p className="text-muted-foreground">Unified view of data quality issues across all detection systems</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleScanCatalog} disabled={scanning}>
            {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            {scanning ? 'Scanning...' : 'Scan Catalog'}
          </Button>
          <Button variant="outline" size="sm" onClick={loadOverview} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="issues">
            Issues {issueCounts?.totals.open ? `(${issueCounts.totals.open})` : ''}
          </TabsTrigger>
          <TabsTrigger value="corrections">Corrections</TabsTrigger>
          <TabsTrigger value="feed-report">Feed Report</TabsTrigger>
          <TabsTrigger value="feed-reports">Feed Reports</TabsTrigger>
        </TabsList>

        {/* ============================================================= */}
        {/* TAB 1: Overview */}
        {/* ============================================================= */}
        <TabsContent value="overview" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Date Range Filter */}
              <div className="flex items-center gap-3">
                <Label className="text-sm text-muted-foreground">From</Label>
                <Input type="date" className="w-[160px]" value={dateRange.from}
                  onChange={(e) => setDateRange(d => ({ ...d, from: e.target.value }))} />
                <Label className="text-sm text-muted-foreground">To</Label>
                <Input type="date" className="w-[160px]" value={dateRange.to}
                  onChange={(e) => setDateRange(d => ({ ...d, to: e.target.value }))} />
                {(dateRange.from || dateRange.to) && (
                  <Button variant="ghost" size="sm" onClick={() => { setDateRange({ from: '', to: '' }); loadOverview(); }}>Clear</Button>
                )}
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Open Issues</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{issueCounts?.totals.open || 0}</div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {(issueCounts?.totals.critical || 0) > 0 && (
                        <Badge className="bg-red-500 text-white text-xs">{issueCounts?.totals.critical} crit</Badge>
                      )}
                      {(issueCounts?.totals.high || 0) > 0 && (
                        <Badge className="bg-orange-500 text-white text-xs">{issueCounts?.totals.high} high</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Correction Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{correctionProgress}%</div>
                    <div className="w-full bg-muted rounded-full h-2 mt-2">
                      <div className="bg-green-500 rounded-full h-2 transition-all" style={{ width: `${correctionProgress}%` }} />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Feed Quality</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">
                      {qualityScore?.feed_quality_score != null ? `${Number(qualityScore.feed_quality_score).toFixed(0)}%` : '—'}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Completeness + correctness</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Enriched Quality</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">
                      {qualityScore?.enriched_quality_score != null ? `${Number(qualityScore.enriched_quality_score).toFixed(0)}%` : '—'}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">After AI enrichment</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Products</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{qualityScore?.total_products?.toLocaleString() || catalogReport?.total_products?.toLocaleString() || '—'}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {qualityScore?.created_at ? `Last scored: ${new Date(qualityScore.created_at).toLocaleDateString()}` : 'Run scan for scores'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Quality Waterfall */}
              {qualityScore && (qualityScore.feed_quality_score != null || qualityScore.enriched_quality_score != null) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Quality Waterfall</CardTitle>
                    <CardDescription>Score improvement through the pipeline</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-8 h-48">
                      {[
                        { label: 'Feed', score: qualityScore.feed_quality_score, color: 'bg-blue-400' },
                        { label: '+ Enrichment', score: qualityScore.enriched_quality_score, color: 'bg-green-400' },
                        { label: '+ Corrections', score: qualityScore.final_quality_score, color: 'bg-emerald-500' },
                      ].map((bar) => (
                        <div key={bar.label} className="flex flex-col items-center gap-2 flex-1">
                          <span className="text-sm font-medium">{bar.score != null ? `${Number(bar.score).toFixed(0)}%` : '—'}</span>
                          <div className="w-full bg-muted rounded-t-md relative" style={{ height: '140px' }}>
                            <div className={`${bar.color} rounded-t-md absolute bottom-0 w-full transition-all duration-500`}
                              style={{ height: bar.score != null ? `${Math.max(Number(bar.score), 5)}%` : '0%' }} />
                          </div>
                          <span className="text-xs text-muted-foreground text-center">{bar.label}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Catalog Report */}
              {catalogReport && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Catalog Breakdown</CardTitle>
                    <CardDescription>
                      {catalogReport.total_products?.toLocaleString()} products across {catalogReport.by_category?.length || 0} categories
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {catalogReport.by_tier?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium mb-2">By Tier</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {catalogReport.by_tier.map((t: any) => (
                            <div key={t.tier || 'unknown'} className="text-center p-3 rounded-lg border">
                              <div className="text-lg font-bold">{t.total_products}</div>
                              <div className="text-xs text-muted-foreground">{t.tier || 'No tier'}</div>
                              {t.completeness_pct != null && (
                                <div className="text-xs mt-1">{Number(t.completeness_pct).toFixed(0)}% complete</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {catalogReport.by_category?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2">Top Categories</h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Category</TableHead>
                              <TableHead className="text-right">Products</TableHead>
                              <TableHead className="text-right">Avg Attrs</TableHead>
                              <TableHead className="text-right">Completeness</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {catalogReport.by_category.slice(0, 15).map((c: any) => (
                              <TableRow key={c.category}>
                                <TableCell className="truncate max-w-[250px]">{c.category}</TableCell>
                                <TableCell className="text-right">{c.total_products}</TableCell>
                                <TableCell className="text-right">{Number(c.avg_attribute_count || 0).toFixed(1)}</TableCell>
                                <TableCell className="text-right">
                                  {c.completeness_pct != null ? (
                                    <span className={Number(c.completeness_pct) < 50 ? 'text-red-500' : Number(c.completeness_pct) < 80 ? 'text-yellow-600' : 'text-green-600'}>
                                      {Number(c.completeness_pct).toFixed(0)}%
                                    </span>
                                  ) : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Breakdowns */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader><CardTitle className="text-base">Issues by Category</CardTitle></CardHeader>
                  <CardContent>
                    {breakdowns?.by_category?.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Category</TableHead>
                            <TableHead className="text-right">Issues</TableHead>
                            <TableHead className="text-right">Critical</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {breakdowns.by_category.map((row: any) => (
                            <TableRow key={row.category}>
                              <TableCell className="truncate max-w-[200px]">{row.category}</TableCell>
                              <TableCell className="text-right">{row.issue_count}</TableCell>
                              <TableCell className="text-right">
                                {parseInt(row.critical_count) > 0 && <Badge variant="destructive">{row.critical_count}</Badge>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : <p className="text-sm text-muted-foreground py-4 text-center">No category data</p>}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-base">Issues by Type</CardTitle></CardHeader>
                  <CardContent>
                    {breakdowns?.by_issue_type?.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Count</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {breakdowns.by_issue_type.map((row: any) => (
                            <TableRow key={row.issue_type}>
                              <TableCell>{row.issue_type}</TableCell>
                              <TableCell className="text-right">{row.issue_count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : <p className="text-sm text-muted-foreground py-4 text-center">No type data</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Source breakdown */}
              {issueCounts?.by_source && Object.keys(issueCounts.by_source).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Issues by Source</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {Object.entries(issueCounts.by_source).map(([source, counts]) => (
                        <div key={source} className="text-center p-3 rounded-lg border">
                          <div className="text-lg font-bold">{counts.open}</div>
                          <div className="text-xs text-muted-foreground">{SOURCE_LABELS[source] || source}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ============================================================= */}
        {/* TAB 2: Issues */}
        {/* ============================================================= */}
        <TabsContent value="issues" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-3 items-center">
                <Select value={issueFilters.source} onValueChange={(v) => setIssueFilters(f => ({ ...f, source: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Sources" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>

                <Select value={issueFilters.severity} onValueChange={(v) => setIssueFilters(f => ({ ...f, severity: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-[130px]"><SelectValue placeholder="All Severities" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severities</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={issueFilters.status} onValueChange={(v) => setIssueFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-[130px]"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={issueFilters.tier} onValueChange={(v) => setIssueFilters(f => ({ ...f, tier: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-[130px]"><SelectValue placeholder="All Tiers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="mega_top">Mega Top</SelectItem>
                    <SelectItem value="super_top">Super Top</SelectItem>
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={issueFilters.aeoStatus} onValueChange={(v) => setIssueFilters(f => ({ ...f, aeoStatus: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-[140px]"><SelectValue placeholder="AEO Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All AEO</SelectItem>
                    <SelectItem value="has_aeo">Has AEO (fix urgent)</SelectItem>
                    <SelectItem value="aeo_target">AEO Target</SelectItem>
                    <SelectItem value="no_aeo">No AEO</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Filter by category..."
                  className="w-[180px]"
                  value={issueFilters.category}
                  onChange={(e) => setIssueFilters(f => ({ ...f, category: e.target.value }))}
                />

                <Select value={issueFilters.sortBy} onValueChange={(v) => setIssueFilters(f => ({ ...f, sortBy: v }))}>
                  <SelectTrigger className="w-[120px]"><SelectValue placeholder="Sort by" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="priority">Priority</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="severity">Severity</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" size="sm" onClick={() => loadIssues(1)}>
                  <SearchIcon className="h-4 w-4 mr-1" /> Apply
                </Button>

                <Button variant="ghost" size="sm" onClick={() => handleExportCSV('issues')}>
                  <Download className="h-4 w-4 mr-1" /> CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Issues Table */}
          <Card>
            <CardContent className="p-0">
              {issuesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : issues.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">No issues found</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[70px]">Priority</TableHead>
                      <TableHead className="w-[90px]">Source</TableHead>
                      <TableHead className="w-[75px]">Severity</TableHead>
                      <TableHead>Product / Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="w-[75px]">Status</TableHead>
                      <TableHead className="w-[140px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {issues.map((issue) => {
                      const key = `${issue.source}-${issue.id}`;
                      const isExpanded = expandedIssue === key;
                      return (
                        <React.Fragment key={key}>
                          <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => handleExpandIssue(issue)}>
                            <TableCell className="font-mono text-sm">{issue.computed_priority}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{SOURCE_LABELS[issue.source] || issue.source}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={SEVERITY_COLORS[issue.severity] || 'bg-gray-500'}>{issue.severity}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-start gap-1">
                                {isExpanded ? <ChevronUp className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                                <div>
                                  <div className="flex items-center gap-1">
                                    {issue.product_name && <span className="font-medium text-sm truncate max-w-[240px]">{issue.product_name}</span>}
                                    {issue.aeo_status === 'has_aeo' && (
                                      <Badge className="bg-purple-600 text-white text-[10px] px-1 py-0">AEO</Badge>
                                    )}
                                    {issue.aeo_status === 'aeo_target' && (
                                      <Badge variant="outline" className="text-purple-600 border-purple-300 text-[10px] px-1 py-0">Target</Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground truncate max-w-[280px]">{issue.description}</div>
                                  {issue.category && <div className="text-xs text-muted-foreground mt-0.5">{issue.category}</div>}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs">{issue.issue_type}</TableCell>
                            <TableCell>
                              <Badge variant={issue.status === 'open' || issue.status === 'pending' ? 'default' : 'secondary'}>
                                {issue.status}
                              </Badge>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <div className="flex gap-1">
                                {(issue.source === 'llm_judge' || issue.source === 'automated') && (
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
                                    onClick={() => {
                                      setCorrectionDialog({ open: true, issue });
                                      setCorrectionForm({ field_name: issue.field_name || '', new_value: issue.expected_value || '' });
                                    }}>Fix</Button>
                                )}
                                {(issue.source === 'llm_judge' || issue.source === 'automated' || issue.source === 'semantic_gap') && (
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
                                    disabled={aiSuggestLoading}
                                    onClick={() => handleAISuggest(issue)}>
                                    {aiSuggestLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3 mr-1" />}
                                    AI
                                  </Button>
                                )}
                                {issue.status === 'open' && (
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
                                    onClick={() => handleUpdateStatus(issue, 'dismissed')}>Dismiss</Button>
                                )}
                                {SOURCE_LINKS[issue.source] && (
                                  <a href={SOURCE_LINKS[issue.source]} className="inline-flex items-center px-2 h-7 text-xs text-muted-foreground hover:text-foreground">
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          {/* Expanded detail */}
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={7} className="bg-muted/30 p-4">
                                {detailLoading ? (
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                ) : issueDetail ? (
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    {issue.field_name && <div><span className="font-medium">Field:</span> {issue.field_name}</div>}
                                    {issue.current_value && <div><span className="font-medium">Current:</span> <span className="text-red-500">{issue.current_value}</span></div>}
                                    {issue.expected_value && <div><span className="font-medium">Expected:</span> <span className="text-green-600">{issue.expected_value}</span></div>}
                                    {issue.tier && <div><span className="font-medium">Tier:</span> {issue.tier}</div>}
                                    <div><span className="font-medium">Created:</span> {new Date(issue.created_at).toLocaleString()}</div>
                                    {issueDetail.diagnosis_notes && <div className="col-span-2"><span className="font-medium">Notes:</span> {issueDetail.diagnosis_notes}</div>}
                                    {issueDetail.affected_content && <div className="col-span-2"><span className="font-medium">Affected content:</span> <p className="mt-1 text-xs bg-muted p-2 rounded">{issueDetail.affected_content}</p></div>}
                                    {issueDetail.suggestion_reasoning && <div className="col-span-2"><span className="font-medium">AI reasoning:</span> {issueDetail.suggestion_reasoning}</div>}
                                  </div>
                                ) : (
                                  <p className="text-muted-foreground">No additional details</p>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>

            {issuesPagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-muted-foreground">
                  {issuesPagination.total} issues ({issuesPagination.page}/{issuesPagination.pages})
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={issuesPagination.page <= 1}
                    onClick={() => loadIssues(issuesPagination.page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                  <Button variant="outline" size="sm" disabled={issuesPagination.page >= issuesPagination.pages}
                    onClick={() => loadIssues(issuesPagination.page + 1)}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ============================================================= */}
        {/* TAB 3: Corrections */}
        {/* ============================================================= */}
        <TabsContent value="corrections" className="space-y-6">
          <div className="flex items-center justify-between">
            <Select value={correctionPeriod} onValueChange={setCorrectionPeriod}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={() => handleExportCSV('corrections')}>
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" /> Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No corrections yet</p>
                ) : (
                  <div className="space-y-3">
                    {leaderboard.map((entry, i) => (
                      <div key={entry.user_email} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-muted-foreground w-6">
                            {i === 0 ? '\u{1F947}' : i === 1 ? '\u{1F948}' : i === 2 ? '\u{1F949}' : `${i + 1}.`}
                          </span>
                          <span className="text-sm">{entry.user_email?.split('@')[0]}</span>
                        </div>
                        <Badge variant="secondary">{entry.correction_count} fixes</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader><CardTitle>Recent Corrections</CardTitle></CardHeader>
              <CardContent>
                {corrections.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No corrections found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Change</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {corrections.slice(0, 20).map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="text-sm">{c.user_email?.split('@')[0] || '—'}</TableCell>
                          <TableCell className="text-sm font-mono">{c.field_name || '—'}</TableCell>
                          <TableCell className="text-xs">
                            {c.old_value && <span className="text-red-500 line-through mr-1">{c.old_value?.substring(0, 30)}</span>}
                            <span className="text-green-600">{c.new_value?.substring(0, 30)}</span>
                          </TableCell>
                          <TableCell className="text-xs">{c.correction_source}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============================================================= */}
        {/* TAB 4: Feed Report */}
        {/* ============================================================= */}
        <TabsContent value="feed-report" className="space-y-6">
          {!feedReport ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              {/* Quality Score Trend */}
              {feedReport.score_history?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Quality Score Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {feedReport.score_history.slice(0, 10).map((s: any, i: number) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground w-[80px]">{new Date(s.created_at).toLocaleDateString()}</span>
                          <div className="flex-1 flex gap-2">
                            {s.feed_quality_score != null && (
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-blue-400" />
                                <span className="text-xs">{Number(s.feed_quality_score).toFixed(0)}%</span>
                              </div>
                            )}
                            {s.enriched_quality_score != null && (
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-green-400" />
                                <span className="text-xs">{Number(s.enriched_quality_score).toFixed(0)}%</span>
                              </div>
                            )}
                            {s.final_quality_score != null && (
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-xs">{Number(s.final_quality_score).toFixed(0)}%</span>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{s.total_products} products</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-400" /> Feed</span>
                      <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-400" /> Enriched</span>
                      <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Final</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Import History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Import History</CardTitle>
                </CardHeader>
                <CardContent>
                  {feedReport.imports?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No imports found</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Products</TableHead>
                          <TableHead className="text-right">New</TableHead>
                          <TableHead className="text-right">Feed</TableHead>
                          <TableHead className="text-right">Enriched</TableHead>
                          <TableHead className="text-right">Final</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {feedReport.imports.map((imp: any) => (
                          <TableRow key={imp.id}>
                            <TableCell><Badge variant={imp.status === 'completed' ? 'default' : 'secondary'}>{imp.status}</Badge></TableCell>
                            <TableCell className="text-right">{imp.total_items || 0}</TableCell>
                            <TableCell className="text-right">{imp.new_items || 0}</TableCell>
                            <TableCell className="text-right">{imp.feed_quality_score != null ? `${Number(imp.feed_quality_score).toFixed(0)}%` : '—'}</TableCell>
                            <TableCell className="text-right">{imp.enriched_quality_score != null ? `${Number(imp.enriched_quality_score).toFixed(0)}%` : '—'}</TableCell>
                            <TableCell className="text-right">{imp.final_quality_score != null ? `${Number(imp.final_quality_score).toFixed(0)}%` : '—'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{new Date(imp.created_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Error Distribution */}
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><BarChart2 className="h-5 w-5" /> Error Type Distribution</CardTitle></CardHeader>
                <CardContent>
                  {feedReport.error_distribution?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No errors found</p>
                  ) : (
                    <div className="space-y-2">
                      {feedReport.error_distribution?.map((err: any) => {
                        const maxCount = Math.max(...feedReport.error_distribution.map((e: any) => parseInt(e.count)));
                        const pct = (parseInt(err.count) / maxCount) * 100;
                        return (
                          <div key={err.issue_type} className="flex items-center gap-3">
                            <span className="text-sm w-[180px] truncate">{err.issue_type}</span>
                            <div className="flex-1 bg-muted rounded-full h-4">
                              <div className="bg-blue-500 rounded-full h-4 transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-sm font-mono w-[50px] text-right">{err.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
        {/* ============================================================= */}
        {/* TAB 5: Feed Reports */}
        {/* ============================================================= */}
        <TabsContent value="feed-reports" className="space-y-4">
          {siteId ? (
            <FeedReportsTab siteId={siteId} />
          ) : (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Select a site to view feed reports.</CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Correction Dialog */}
      <Dialog open={correctionDialog.open} onOpenChange={(open) => setCorrectionDialog({ open, issue: open ? correctionDialog.issue : null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Correct Data Issue</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {correctionDialog.issue && (
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{correctionDialog.issue.product_name}</p>
                <p>{correctionDialog.issue.description}</p>
                {correctionDialog.issue.current_value && (
                  <p className="mt-1">Current value: <span className="text-red-500">{correctionDialog.issue.current_value}</span></p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Field Name</Label>
              <Input value={correctionForm.field_name}
                onChange={(e) => setCorrectionForm(f => ({ ...f, field_name: e.target.value }))}
                placeholder="e.g. outer_material_type" />
            </div>
            <div className="space-y-2">
              <Label>Correct Value</Label>
              <Textarea value={correctionForm.new_value}
                onChange={(e) => setCorrectionForm(f => ({ ...f, new_value: e.target.value }))}
                placeholder="Enter the correct value" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCorrectionDialog({ open: false, issue: null })}>Cancel</Button>
              <Button onClick={handleCorrectSubmit}>Apply Correction</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
